import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import ru from './locales/ru.json';
import zh from './locales/zh.json';

export const SUPPORTED_LOCALES = ['en', 'ru', 'zh'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'tacita.lang';

function isSupported(value: string | null): value is Locale {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Язык, с которого начинается сессия: ранее выбранный клиентом, иначе — язык браузера.
 *
 * Перебираем ВЕСЬ `navigator.languages` по порядку предпочтения, а не только первый: у клиента вполне может
 * стоять ['fr-CH', 'ru', 'en'], и правильный ответ там «ru», а не «языка нет». Клиентура разноязычная, поэтому
 * фиксированного языка здесь быть не должно; неизвестный язык падает в `en` — тот же fallback, что у писем
 * (`EmailRenderer.SUPPORTED`), чтобы интерфейс и уведомления не разъезжались.
 */
export function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const persisted = window.localStorage.getItem(STORAGE_KEY);
  if (isSupported(persisted)) return persisted;
  return browserLocale() ?? 'en';
}

/** Первый из языков браузера, который мы умеем показывать и на котором пишем письма; null — ни одного. */
export function browserLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  const nav = window.navigator;
  const candidates = nav.languages?.length ? nav.languages : [nav.language ?? ''];
  for (const c of candidates) {
    const lang = c.slice(0, 2).toLowerCase();
    if (isSupported(lang)) return lang;
  }
  return null;
}

export function persistLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, locale);
  document.documentElement.setAttribute('lang', locale);
}

/** CLDR Russian plural category of {@code n} as an index into "one | few | many". */
export function russianPlural(n: number, choicesLength: number): number {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  const index = mod10 === 1 && mod100 !== 11 ? 0
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 1
      : 2;
  return Math.min(index, choicesLength - 1);
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectInitialLocale(),
  fallbackLocale: 'en',
  messages: { en, ru, zh },
  // Russian has three plural forms (1/21 минута · 2–4 минуты · 5–20 минут); vue-i18n's default knows only two.
  // A ru message with plurals lists them in that order: "one | few | many".
  pluralRules: { ru: russianPlural },
  datetimeFormats: {
    en: {
      short:     { day: 'numeric', month: 'short' },
      shortTime: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
      long:      { day: 'numeric', month: 'long', year: 'numeric' },
      time:      { hour: '2-digit', minute: '2-digit' },
    },
    ru: {
      short:     { day: 'numeric', month: 'short' },
      shortTime: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
      long:      { day: 'numeric', month: 'long', year: 'numeric' },
      time:      { hour: '2-digit', minute: '2-digit' },
    },
    zh: {
      short:     { day: 'numeric', month: 'short' },
      shortTime: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
      long:      { dateStyle: 'long' },
      time:      { hour: '2-digit', minute: '2-digit' },
    },
  },
  numberFormats: {
    en: { currency: { style: 'currency', currency: 'USD' } },
    ru: { currency: { style: 'currency', currency: 'RUB' } },
    zh: { currency: { style: 'currency', currency: 'CNY' } },
  },
});

export function setLocale(next: Locale): void {
  i18n.global.locale.value = next;
  persistLocale(next);
}

// Apply initial lang attribute on bootstrap.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', i18n.global.locale.value);
}
